<template>
    <section class="banner-bg-section">
        <div class="banner-bg-section__container">
            <!-- 图片循环滚动区域 -->
            <div class="banner-bg__marquee">
                <!-- 左侧渐变遮罩 -->
                <div class="banner-bg__mask banner-bg__mask--left"></div>
                <!-- 右侧渐变遮罩 -->
                <div class="banner-bg__mask banner-bg__mask--right"></div>

                <!-- 第一组图片（用于无缝循环） -->
                <div class="banner-bg__track">
                    <div v-for="(cell, idx) in props.item.fieldData || []"
                        :key="'a-' + String((cell as Record<string, unknown>)?.id ?? idx)" class="banner-bg__img-wrap">
                        <img v-if="getCellImage(cell)" :src="getCellImage(cell)" :alt="centerTitle || ''"
                            loading="lazy" />
                    </div>
                    <!-- 复制一份实现无缝循环 -->
                    <div v-for="(cell, idx) in props.item.fieldData || []"
                        :key="'b-' + String((cell as Record<string, unknown>)?.id ?? idx)" class="banner-bg__img-wrap">
                        <img v-if="getCellImage(cell)" :src="getCellImage(cell)" :alt="centerTitle || ''"
                            loading="lazy" />
                    </div>
                </div>

                <!-- 居中文字框 -->
                <div v-if="!hideTitle && (centerTitle || centerSubtitle)" class="banner-bg__center-box">
                    <h2 v-if="centerTitle" class="banner-bg__center-title">
                        {{ centerTitle }}
                    </h2>
                    <p v-if="centerSubtitle" class="banner-bg__center-subtitle">{{ centerSubtitle }}</p>
                </div>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { Module } from '@/api/page_config'
import { getStrWithRecord } from './index';

const props = withDefaults(defineProps<{
    item: Module
    hideTitle?: boolean
}>(), { hideTitle: false })

const centerTitle = computed<string>(() => {
    return props.item.mainTitle || ''
})
const centerSubtitle = computed<string>(() => {
    return props.item.subTitle || ''
})

function getCellImage(cell: unknown): string {
    return getStrWithRecord(cell, 'image') || ''
}

</script>

<style scoped lang="scss">
@use '@/assets/styles/variables' as *;

.banner-bg-section {
    width: 100%;
    overflow: hidden;
    aspect-ratio: 1920 / 546;
}

.banner-bg-section__container {
    max-width: 100%;
    height: 100%;
    margin: 0;
    padding: 0;
}

.banner-bg__marquee {
    position: relative;
    width: 100%;
    overflow: hidden;
    height: 100%;
}

/* 两侧渐变遮罩 */
.banner-bg__mask {
    position: absolute;
    top: 0;
    bottom: 0;
    width: 15%;
    z-index: 2;
    pointer-events: none;

    &--left {
        left: 0;
        background: linear-gradient(to right, #f8f9fb 0%, rgba(248, 249, 251, 0) 100%);
    }

    &--right {
        right: 0;
        background: linear-gradient(to left, #f8f9fb 0%, rgba(248, 249, 251, 0) 100%);
    }
}

/* 滚动轨道 */
.banner-bg__track {
    display: flex;
    gap: 1rem;
    animation: marquee-scroll 30s linear infinite;
    width: max-content;
    height: 100%;
    width: 100%;

    @media (min-width: $breakpoint-md) {
        gap: 1.25rem;
    }

    &:hover {
        animation-play-state: paused;
    }
}

.banner-bg__img-wrap {
    flex-shrink: 0;
    width: 100%;
    height: 100%;
    border-radius: 0.75rem;
    overflow: hidden;
    background: $color-bg-tertiary;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);

    @media (min-width: $breakpoint-md) {
        width: 100%;
        height: 100%;
        border-radius: 1rem;
    }

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
    }
}

@keyframes marquee-scroll {
    0% {
        transform: translateX(0);
    }

    100% {
        transform: translateX(-50%);
    }
}

/* 居中文字框 */
.banner-bg__center-box {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 3;
    background: rgba(255, 255, 255, 0.6);
    backdrop-filter: blur(15px);
    box-shadow: 0px 6px 15px 0px rgba(0, 0, 0, 0.3);
    border-radius: 1rem;
    padding: 1.5rem 2.5rem;
    text-align: center;
    min-width: 260px;
    max-width: 85%;

    @media (min-width: $breakpoint-md) {
        padding: 2.25rem 4rem;
        border-radius: 1.25rem;
        min-width: 440px;
    }
}

.banner-bg__center-title {
    font-size: 1.1rem;
    font-weight: 800;
    color: $color-text;
    margin: 0;
    line-height: 1.3;
    letter-spacing: -0.02em;

    @media (min-width: $breakpoint-md) {
        font-size: 2.25rem;
    }
}

.banner-bg__center-subtitle {
    font-size: 0.875rem;
    color: $color-text-secondary;
    margin: 0.5rem 0 0;
    line-height: 1.5;

    @media (min-width: $breakpoint-md) {
        font-size: 1rem;
        margin-top: 0.75rem;
    }
}
</style>
